import React from 'react';
import PropTypes from 'prop-types';
import './LoadingSystem.css';

/**
 * Spinner - Indicador de carga circular animado
 * 
 * Casos de uso:
 * - Acciones de botones (enviar, guardar, eliminar)
 * - Carga de datos en secciones pequeñas
 * - Estados de carga en línea con texto
 * 
 * Mejores prácticas:
 * - Usar 'small' (16px) para botones
 * - Usar 'medium' (24px) para indicadores inline
 * - Usar 'large' (40px) para áreas de contenido más grandes
 * 
 * @example
 * // En un botón
 * <button disabled={loading}>
 *   {loading ? <Spinner size="small" /> : 'Guardar'}
 * </button>
 * 
 * @example
 * // En una sección de contenido
 * {loading ? (
 *   <div style={{ textAlign: 'center', padding: '20px' }}>
 *     <Spinner size="medium" text="Cargando datos..." />
 *   </div>
 * ) : (
 *   <Content />
 * )}
 */
const Spinner = ({ 
  size = 'medium', 
  color = 'primary',
  text = '',
  className = '',
  centered = false,
  fullHeight = false
}) => {
  const containerClass = `
    loading-spinner-container
    ${centered ? 'loading-spinner-centered' : ''}
    ${fullHeight ? 'loading-spinner-full-height' : ''}
    ${className}
  `.trim();

  return (
    <div className={containerClass}>
      <div 
        className={`loading-spinner loading-spinner--${size} loading-spinner--${color}`}
        role="status"
        aria-label="Cargando"
      >
        <div className="loading-spinner__circle"></div>
      </div>
      {text && <p className="loading-spinner__text">{text}</p>}
    </div>
  );
};

Spinner.propTypes = {
  /** Tamaño del spinner: 'small' (16px), 'medium' (24px), 'large' (40px), 'xlarge' (56px) */
  size: PropTypes.oneOf(['small', 'medium', 'large', 'xlarge']),
  /** Color del spinner: 'primary', 'secondary', 'accent' */
  color: PropTypes.oneOf(['primary', 'secondary', 'accent', 'white']),
  /** Texto opcional que se muestra debajo del spinner */
  text: PropTypes.string,
  /** Clase CSS adicional */
  className: PropTypes.string,
  /** Centrar el spinner horizontal y verticalmente */
  centered: PropTypes.bool,
  /** Ocupar toda la altura disponible del contenedor padre */
  fullHeight: PropTypes.bool,
};

export default Spinner;
